import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import '../css/Members.css'; // This now links to your new premium styles

// --- SVG Icons ---
const SearchIcon = () => (
    <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
);
const BackIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
    </svg>
);
const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const StudentsData = () => {
    const [participants, setParticipants] = useState([]);
    const [colleges, setColleges] = useState([]);
    const [filteredColleges, setFilteredColleges] = useState([]);
    const [selectedCollege, setSelectedCollege] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [participantSearchText, setParticipantSearchText] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userName, setUserName] = useState('');
    const [userRole, setUserRole] = useState('');
    const [campRegistrations, setCampRegistrations] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [selectedParticipantDetail, setSelectedParticipantDetail] = useState(null);
    const [facilitatorsData, setFacilitatorsData] = useState([]); // New state to store all facilitators
    const [areaLeadersData, setAreaLeadersData] = useState([]); // New state to store all area leaders

    // UI State Management
    const [showColleges, setShowColleges] = useState(true);
    const [showFacilitatorsForCollege, setShowFacilitatorsForCollege] = useState(false);
    const [showStudentsForFacilitator, setShowStudentsForFacilitator] = useState(false);
    const [selectedFacilitator, setSelectedFacilitator] = useState(null); // The facilitator whose students are being viewed
    const [selectedFacilitatorDetail, setSelectedFacilitatorDetail] = useState(null); // The facilitator whose details are being viewed

    // Fetch user email and role from cookies on component mount
    useEffect(() => {
        const email = Cookies.get('email');
        const role = Cookies.get('role');
        if (email && role) {
            setUserEmail(email);
            setUserRole(role);
        } else {
            Swal.fire({
                icon: 'error',
                title: 'Access Denied',
                text: 'Please log in to view student data.',
            });
            // Optionally redirect to login page
            // window.location.href = '/login';
        }
    }, []);

    // Fetch user-specific data (mentor name for facilitator, all facilitators for area leader)
    useEffect(() => {
        if (userEmail && userRole) {
            if (userRole === 'facilitator') {
                fetchFacilitatorData(userEmail);
            } else if (userRole === 'area_leader') {
                fetchFacilitatorData(userEmail); // To get all facilitators data
                fetchAllFacilitatorsAndFilter(userEmail); // To get area leader's own data
            } else if (userRole === 'admin') {
                // Admin doesn't need a specific name, just fetch all facilitators
                fetchFacilitatorData(userEmail); // This will fetch all facilitators into facilitatorsData
            }
        }
    }, [userEmail, userRole]);

    // Fetch reports and camp registrations once userName is set (for both roles) or if admin
    useEffect(() => {
        if (userRole === 'admin' || userName) {
            fetchCampRegistrations();
            fetchReports();
            fetchData(); // Facilitator / Area Leader / Admin with valid name/role
        }
    }, [userName, userRole]);

    // Filter colleges based on search text
    useEffect(() => {
        const filtered = colleges.filter((college) =>
            college.toLowerCase().includes(searchText.toLowerCase())
        );
        setFilteredColleges(filtered);
    }, [searchText, colleges]);

    const fetchFacilitatorData = async (email) => {
        try {
            const participantsMentorRes = await fetch('https://ethiccraft.org/backend/Facilitator_Panel/read.php');
            if (!participantsMentorRes.ok) {
                throw new Error(`HTTP error! status: ${participantsMentorRes.status}`);
            }

            const text = await participantsMentorRes.text();
            if (!text) {
                throw new Error('Empty response body');
            }

            const participantsMentorData = JSON.parse(text);
            setFacilitatorsData(participantsMentorData); // Store all facilitators for potential area leader/admin use

            if (userRole === 'facilitator') {
                const facilitator = participantsMentorData.find((p) => p.Email === email);
                if (facilitator) {
                    if (facilitator.Approved === "1") {
                        setUserName(facilitator.Full_Name);
                    } else {
                        Swal.fire({
                            icon: 'warning',
                            title: 'Not Approved',
                            text: 'You are not approved to access student data.',
                        });
                    }
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'User Not Found',
                        text: 'No facilitator found with the provided email.',
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching facilitator data:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch facilitator data.',
            });
        }
    };

    const fetchAllFacilitatorsAndFilter = async (areaLeaderEmail) => {
        try {
            const res = await fetch('https://ethiccraft.org/backend/Facilitator_Panel/get_area_leaders.php');
            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }
            const data = await res.json();
            setAreaLeadersData(data); // Store all area leaders

            const areaLeader = data.find(f => f.email === areaLeaderEmail);
            if (areaLeader && areaLeader.Approved === "1") {
                setUserName(areaLeader.full_name); // Set leader's name for display
            } else {
                Swal.fire({
                    icon: 'warning',
                    title: 'Not Approved',
                    text: 'You are not approved to access student data as an Area Leader.',
                });
            }
        } catch (error) {
            console.error('Error fetching all facilitators:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch facilitator data for area leader.',
            });
        }
    };

    const fetchCampRegistrations = async () => {
        try {
            const res = await fetch('https://ethiccraft.org/backend/Facilitator_Panel/Camp_Registration_Check.php');
            const data = await res.json();
            setCampRegistrations(data);
        } catch (error) {
            console.error('Error fetching camp registrations:', error);
        }
    };

    const getRegisteredCamps = (participant) => {
        const registrations = campRegistrations.find(
            (camp) => camp.email === participant.email
        );
        if (registrations && Array.isArray(registrations.camps)) {
            return registrations.camps.map(camp => camp.camp_name);
        }
        return [];
    };

    const fetchReports = async () => {
        try {
            const res = await fetch('https://ethiccraft.org/backend/Facilitator_Panel/Read_Report.php');
            const data = await res.json();
            setReportData(data);
        } catch (error) {
            console.error('Error fetching reports:', error);
        }
    };

    const fetchData = async () => {
        try {
            const participantsRes = await fetch('https://ethiccraft.org/backend/profile/read.php');
            const participantsData = await participantsRes.json();

            let filteredParticipantsData = [];

            if (userRole === 'facilitator') {
                filteredParticipantsData = participantsData.filter((p) => p.Mentors_Name === userName);
            } else if (userRole === 'area_leader') {
                const areaLeaderSelf = areaLeadersData.find(f => f.email === userEmail);
                if (areaLeaderSelf && areaLeaderSelf.Area) {
                    const facilitatorsInArea = facilitatorsData.filter((f) => f.Area === areaLeaderSelf.Area && f.Approved === "1");
                    const facilitatorNamesInArea = new Set(facilitatorsInArea.map(f => f.Full_Name));
                    filteredParticipantsData = participantsData.filter(p => facilitatorNamesInArea.has(p.Mentors_Name));
                }
            } else if (userRole === 'admin') {
                filteredParticipantsData = participantsData; // Admin can see all participants
            }

            setParticipants(filteredParticipantsData);
            const uniqueColleges = [...new Set(filteredParticipantsData.map(p => p.college).filter(Boolean))];
            setColleges(uniqueColleges);
            setFilteredColleges(uniqueColleges);
        } catch (error) {
            console.error('Error fetching data:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch student data.',
            });
        }
    };

    const handleViewListClick = (college) => {
        setSelectedCollege(college);
        if (userRole === 'facilitator') {
            setShowColleges(false);
            setShowFacilitatorsForCollege(false); // Ensure this is false
            setShowStudentsForFacilitator(true); // Show students directly for facilitator
            setParticipantSearchText(''); // Clear search when changing view
        } else if (userRole === 'area_leader' || userRole === 'admin') {
            setShowColleges(false);
            setShowFacilitatorsForCollege(true);
            setShowStudentsForFacilitator(false); // Ensure this is false
            setSelectedFacilitator(null); // Reset selected facilitator
            setParticipantSearchText(''); // Clear search when changing view
        }
    };

    const handleBackToColleges = () => {
        setShowColleges(true);
        setShowFacilitatorsForCollege(false);
        setShowStudentsForFacilitator(false);
        setSelectedCollege(null);
        setSelectedFacilitator(null);
        setSearchText(''); // Clear college search
        setParticipantSearchText(''); // Clear participant search
    };

    const handleBackToFacilitators = () => {
        setShowFacilitatorsForCollege(true);
        setShowStudentsForFacilitator(false);
        setSelectedFacilitator(null);
        setParticipantSearchText(''); // Clear participant search
    };

    const handleViewDetailClick = (participant) => {
        const reportEntry = reportData.find(r => r.Student_ID === participant.user_id);
        setSelectedParticipantDetail({
            ...participant,
            Need: reportEntry?.Need || 'N/A',
            Interest: reportEntry?.Interest || 'N/A',
            Concern: reportEntry?.Concern || 'N/A', 
            Remarks: reportEntry?.Remarks || 'N/A'
        });
    };

    const handleCloseDetailModal = () => {
        setSelectedParticipantDetail(null);
    };

    const handleViewFacilitatorDetailClick = (facilitator) => {
        setSelectedFacilitatorDetail(facilitator);
    };

    const handleCloseFacilitatorDetailModal = () => {
        setSelectedFacilitatorDetail(null);
    };

    const handleViewStudentsForFacilitatorClick = (facilitator) => {
        setSelectedFacilitator(facilitator); // Store the facilitator whose students we want to see
        setShowStudentsForFacilitator(true);
        setShowFacilitatorsForCollege(false); // Hide facilitator list
        setParticipantSearchText(''); // Clear student search
    };

    // --- Approval Logic ---
    const handleApproveParticipant = async (participantId) => {
        try {
            const formData = new FormData();
            formData.append('user_id', participantId);

            const response = await fetch('https://ethiccraft.org/backend/Facilitator_Panel/update_approval.php', {
                method: 'POST',
                body: formData,
            });

            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("application/json") !== -1) {
                const result = await response.json();
                if (result.success) {
                    Swal.fire('Approved!', 'Participant has been approved.', 'success');
                    // Update the state to reflect the approval
                    setParticipants((prev) =>
                        prev.map((item) =>
                            item.user_id === participantId ? { ...item, FacilitatorApproved: '1' } : item
                        )
                    );
                } else {
                    Swal.fire('Error', result.message || 'Failed to approve. Try again.', 'error');
                }
            } else {
                const textResult = await response.text();
                if (response.ok) {
                    Swal.fire('Approved!', 'Participant has been approved.', 'success');
                    setParticipants((prev) =>
                        prev.map((item) =>
                            item.user_id === participantId ? { ...item, FacilitatorApproved: '1' } : item
                        )
                    );
                } else {
                    Swal.fire('Error', textResult || 'Something went wrong during approval!', 'error');
                }
            }
        } catch (error) {
            console.error('Error approving participant:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Something went wrong while trying to approve the participant!',
            });
        }
    };

    // Helper function to get facilitators relevant to the selected college and user role
    const getFacilitatorsForSelectedCollege = () => {
        if (!selectedCollege || !facilitatorsData.length) return [];

        // 1. Find all unique mentor names (facilitators) who have students in the selected college
        const mentorsInSelectedCollege = new Set(
            participants
                .filter(p => p.college === selectedCollege)
                .map(p => p.Mentors_Name)
                .filter(Boolean) // Remove any null/undefined mentor names
        );

        // 2. Filter the full facilitatorsData to get only those mentors
        let relevantFacilitators = facilitatorsData.filter(f => mentorsInSelectedCollege.has(f.Full_Name));

        // 3. For area_leader, further filter by their area
        if (userRole === 'area_leader') {
            const areaLeaderSelf = areaLeadersData.find(f => f.email === userEmail);
            if (areaLeaderSelf && areaLeaderSelf.Area) {
                relevantFacilitators = relevantFacilitators.filter(f => f.Area === areaLeaderSelf.Area);
            }
        }
        // For admin, no further filtering needed, they see all relevant facilitators

        return relevantFacilitators;
    };

    return (
        <div className="main">
            <div className="data-container">
                {showColleges && (
                    <>
                        {/* Colleges Section */}
                        <div className="container-header">
                            <h1 className="header-title">Colleges</h1>
                            <div className="search-box">
                                <SearchIcon />
                                <input
                                    type="text"
                                    placeholder="Search colleges..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    className="search-input"
                                />
                            </div>
                        </div>
                        <div className="table-wrapper">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Sr. No.</th>
                                        <th>College</th>
                                        <th>Total Members</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredColleges.map((college, index) => {
                                        const totalMembers = participants.filter((p) => p.college === college).length;
                                        return (
                                            <tr key={college}>
                                                <td>{index + 1}</td>
                                                <td>{college}</td>
                                                <td>{totalMembers}</td>
                                                <td>
                                                    <button className="btn btn-primary" onClick={() => handleViewListClick(college)}>
                                                        View List
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {showFacilitatorsForCollege && (userRole === 'area_leader' || userRole === 'admin') && (
                    <>
                        {/* Facilitators Section for Area Leader/Admin */}
                        <div className="container-header">
                            <h1 className="header-title">Facilitators for {selectedCollege}</h1>
                            <div className="header-actions">
                                <button onClick={handleBackToColleges} className="btn btn-secondary">
                                    <BackIcon /> Back to Colleges
                                </button>
                            </div>
                        </div>
                        <div className="table-wrapper">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Sr. No.</th>
                                        <th>Facilitator Name</th>
                                        <th>No. of Students</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getFacilitatorsForSelectedCollege().map((facilitator, index) => {
                                        const studentsCount = participants.filter(
                                            p => p.Mentors_Name === facilitator.Full_Name && p.college === selectedCollege
                                        ).length;
                                        return (
                                            <tr key={facilitator.Email}>
                                                <td>{index + 1}</td>
                                                <td>{facilitator.Full_Name}</td>
                                                <td>{studentsCount}</td>
                                                <td>
                                                    <button className="btn btn-primary" onClick={() => handleViewFacilitatorDetailClick(facilitator)}>
                                                        View Facilitator Details
                                                    </button>
                                                    <button className="btn btn-info" onClick={() => handleViewStudentsForFacilitatorClick(facilitator)}>
                                                        View Students
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {showStudentsForFacilitator && (
                    <>
                        {/* Participants Section */}
                        <div className="container-header">
                            <h1 className="header-title">
                                {userRole === 'facilitator' ? `Students for ${selectedCollege}` : `Students for ${selectedFacilitator?.Full_Name}`}
                            </h1>
                            <div className="header-actions">
                                <div className="search-box">
                                    <SearchIcon />
                                    <input
                                        type="text"
                                        placeholder="Search Members..."
                                        value={participantSearchText}
                                        onChange={(e) => setParticipantSearchText(e.target.value)}
                                        className="search-input"
                                    />
                                </div>
                                <button onClick={userRole === 'facilitator' ? handleBackToColleges : handleBackToFacilitators} className="btn btn-secondary">
                                    <BackIcon /> Back
                                </button>
                            </div>
                        </div>
                        <div className="table-wrapper">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Full Name</th>
                                        <th>Contact</th>
                                        <th>Status</th>
                                        <th>Camp</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {participants
                                        .filter(p => p.college === selectedCollege) // Filter by selected college
                                        .filter(p => {
                                            // If a specific facilitator's students are being viewed (area_leader/admin)
                                            if (selectedFacilitator) {
                                                return p.Mentors_Name === selectedFacilitator.Full_Name;
                                            }
                                            // Otherwise (for facilitator role), `participants` is already scoped to their students
                                            return true;
                                        })
                                        .filter((p) => { // Apply search text
                                            const nameMatch = p.full_name && p.full_name.toLowerCase().includes(participantSearchText.toLowerCase());
                                            const emailMatch = p.email && p.email.toLowerCase().includes(participantSearchText.toLowerCase());
                                            const phoneMatch = p.phone_number && p.phone_number.toLowerCase().includes(participantSearchText.toLowerCase());
                                            const idMatch = p.user_id && p.user_id.toString().includes(participantSearchText);
                                            return nameMatch || emailMatch || phoneMatch || idMatch;
                                        })
                                        .map((p) => (
                                            <tr key={p.user_id}>
                                                <td>{p.user_id}</td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <img src={p.profile_image ? `https://ethiccraft.org/${p.profile_image}` : `https://ui-avatars.com/api/?name=${p.full_name}&background=random`} alt="Profile" className="profile-image-small" />
                                                        <span>{p.full_name}</span>
                                                    </div>
                                                </td>
                                                <td>{p.email}</td>
                                                <td>
                                                    {p.FacilitatorApproved === '1' ? (
                                                        <span className="status-pill status-approved">Approved</span>
                                                    ) : (
                                                        // Only show approve button if the user is a facilitator
                                                        userRole === 'facilitator' ? (
                                                            <button
                                                                className="btn btn-success"
                                                                onClick={() => handleApproveParticipant(p.user_id)}
                                                            >
                                                                Approve
                                                            </button>
                                                        ) : (
                                                            <span className="status-pill status-pending">Not Approved Yet</span>
                                                        )
                                                    )}
                                                </td>
                                                <td>
                                                    {getRegisteredCamps(p).length > 0
                                                        ? <span className="status-pill status-registered">{getRegisteredCamps(p).join(', ')}</span>
                                                        : <span className="status-pill status-not-registered">Not Registered</span>
                                                    }
                                                </td>
                                                <td>
                                                    <button className="btn btn-primary" onClick={() => handleViewDetailClick(p)}>
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {selectedParticipantDetail && (
                <div className="modal-overlay" onClick={handleCloseDetailModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="premium-close-button" onClick={handleCloseDetailModal}><svg class="close-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <div className="modal-header">
                            <h2>Member Details</h2>
                        </div>
                        <div className="modal-body">
                            <div className="profile-main-info">
                                <img
                                    src={selectedParticipantDetail.profile_image ? `https://ethiccraft.org/${selectedParticipantDetail.profile_image}` : `https://ui-avatars.com/api/?name=${selectedParticipantDetail.full_name}&background=random`}
                                    alt="Profile"
                                    className="profile-image-large"
                                />
                                <div className="profile-name-email">
                                    <h3>{selectedParticipantDetail.full_name}</h3>
                                    <p>{selectedParticipantDetail.email}</p>
                                    <p>Last Updated : {selectedParticipantDetail.updated_at}</p>
                                </div>
                            </div>
                            <div className="profile-details">
                                <div className="profile-detail"><strong>Phone:</strong> {selectedParticipantDetail.phone_number}</div>
                                <div className="profile-detail"><strong>Gender:</strong> {selectedParticipantDetail.gender}</div>
                                <div className="profile-detail"><strong>Department of Study:</strong> {selectedParticipantDetail.department}</div>
                                <div className="profile-detail"><strong>College:</strong> {selectedParticipantDetail.college}</div>
                                <div className="profile-detail"><strong>Address:</strong> {selectedParticipantDetail.address}</div>
                                <div className="profile-detail"><strong>Chanting:</strong> {selectedParticipantDetail.Chanting} Rounds</div>
                                <div className="profile-detail"><strong>Sincerity:</strong> {selectedParticipantDetail.sincerity}%</div>
                                <div className="profile-detail"><strong>Need:</strong> {selectedParticipantDetail.Need}</div>
                                <div className="profile-detail"><strong>Interest:</strong> {selectedParticipantDetail.Interest}</div>
                                <div className="profile-detail"><strong>Concern:</strong> {selectedParticipantDetail.Concern}</div>
                                <div className="profile-detail"><strong>Remarks:</strong> {selectedParticipantDetail.Remarks}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedFacilitatorDetail && (userRole === 'area_leader' || userRole === 'admin') && (
                <div className="modal-overlay" onClick={handleCloseFacilitatorDetailModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="premium-close-button" onClick={handleCloseFacilitatorDetailModal}><svg class="close-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <div className="modal-header">
                            <h2>Facilitator Details</h2>
                        </div>
                        <div className="modal-body">
                            <div className="profile-main-info">
                                <img
                                    src={`https://ui-avatars.com/api/?name=${selectedFacilitatorDetail.Full_Name}&background=random`}
                                    alt="Facilitator Profile"
                                    className="profile-image-large"
                                />
                                <div className="profile-name-email">
                                    <h3>{selectedFacilitatorDetail.Full_Name}</h3>
                                    <p>{selectedFacilitatorDetail.Email}</p>
                                    <p>Area: {selectedFacilitatorDetail.Area}</p>
                                </div>
                            </div>
                            <div className="profile-details">
                                <div className="profile-detail"><strong>Phone:</strong> {selectedFacilitatorDetail.Mobile_Number}</div>
                                <div className="profile-detail"><strong>Approved:</strong> {selectedFacilitatorDetail.Approved === "1" ? "Yes" : "No"}</div>
                                {/* Add other relevant facilitator details here if available in facilitatorsData */}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentsData;